import {
  Box,
  Text,
  Image,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { fetchPostBySlug } from "../github";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { useLoaderData } from "react-router-dom";

export function slugify(title) {
  return title
    .toLowerCase()
    .replace(/[—–]/g, "-")
    .replace(/&/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const JF = "'Josefin Sans', sans-serif";
const PJ = "'Plus Jakarta Sans', sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace";

function useIsDark() {
  const bg = useColorModeValue("light", "dark");
  return bg === "dark";
}

function CodeBlock({ inline, className, children }) {
  const isDark = useIsDark();
  const inlineBg = useColorModeValue("#f0f0f0", "#2a2a2a");
  const inlineColor = useColorModeValue("#c7254e", "#e06c75");

  const match = /language-(\w+)/.exec(className || "");
  const lang = match ? match[1] : "text";
  const code = String(children).replace(/\n$/, "");

  if (inline) {
    return (
      <Box
        as="code"
        bg={inlineBg}
        color={inlineColor}
        px="5px"
        py="1px"
        borderRadius="4px"
        fontFamily={MONO}
        fontSize="0.85em"
        letterSpacing="0.01em"
      >
        {children}
      </Box>
    );
  }

  // Detect BEFORE/AFTER label from comment in first line
  const firstLine = code.split("\n")[0] ?? "";
  const label = firstLine.match(/\/\/\s*(BEFORE|AFTER)/i)?.[1] ?? null;

  return (
    <Box
      position="relative"
      my={6}
      borderRadius="10px"
      overflow="hidden"
      border="1px solid"
      borderColor={isDark ? "#2e2e2e" : "#e2e2e2"}
      boxShadow={isDark ? "0 4px 24px rgba(0,0,0,0.45)" : "0 2px 12px rgba(0,0,0,0.08)"}
    >
      {/* Top bar mimicking a terminal chrome */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px="14px"
        py="8px"
        bg={isDark ? "#1a1a1a" : "#f5f5f5"}
        borderBottom="1px solid"
        borderColor={isDark ? "#2e2e2e" : "#e2e2e2"}
      >
        <Box display="flex" gap="6px" alignItems="center">
          <Box w="10px" h="10px" borderRadius="full" bg="#ff5f57" />
          <Box w="10px" h="10px" borderRadius="full" bg="#febc2e" />
          <Box w="10px" h="10px" borderRadius="full" bg="#28c840" />
        </Box>
        <Box display="flex" alignItems="center" gap="10px">
          {label && (
            <Box
              as="span"
              fontFamily={JF}
              fontSize="10px"
              letterSpacing="0.15em"
              textTransform="uppercase"
              fontWeight="700"
              px="8px"
              py="2px"
              borderRadius="4px"
              bg={label === "BEFORE" ? (isDark ? "#3a1a1a" : "#fff0f0") : (isDark ? "#1a3a1a" : "#f0fff0")}
              color={label === "BEFORE" ? (isDark ? "#ff6b6b" : "#c0392b") : (isDark ? "#69db7c" : "#27ae60")}
              border="1px solid"
              borderColor={label === "BEFORE" ? (isDark ? "#5a2a2a" : "#f5c6cb") : (isDark ? "#2a5a2a" : "#c3e6cb")}
            >
              {label}
            </Box>
          )}
          <Box
            as="span"
            fontFamily={MONO}
            fontSize="11px"
            color={isDark ? "#555" : "#aaa"}
            textTransform="lowercase"
          >
            {lang}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          "& pre": { margin: "0 !important", borderRadius: "0 !important" },
          "& pre > code": { fontFamily: `${MONO} !important`, fontSize: "13.5px !important", lineHeight: "1.75 !important" },
        }}
      >
        <SyntaxHighlighter
          language={lang === "text" ? "typescript" : lang}
          style={isDark ? oneDark : oneLight}
          showLineNumbers={!inline && code.split("\n").length > 3}
          wrapLines={true}
          customStyle={{
            margin: 0,
            padding: "18px 16px",
            background: isDark ? "#141414" : "#fafafa",
            fontSize: "13.5px",
            lineHeight: "1.75",
          }}
          lineNumberStyle={{
            color: isDark ? "#3a3a3a" : "#d0d0d0",
            minWidth: "2.5em",
            userSelect: "none",
          }}
        >
          {code}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
}

function MarkdownTable({ children }) {
  const borderColor = useColorModeValue("#e2e8f0", "#2d3748");
  const headBg = useColorModeValue("#f7fafc", "#1a202c");
  const headColor = useColorModeValue("#2d3748", "#e2e8f0");
  const rowHoverBg = useColorModeValue("#f7fafc", "#1a202c");

  return (
    <Box
      overflowX="auto"
      my={6}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="8px"
      sx={{
        "& table": { width: "100%", borderCollapse: "collapse" },
        "& th, & td": {
          padding: "10px 14px",
          borderBottom: `1px solid`,
          borderColor: borderColor,
          fontFamily: PJ,
          fontSize: "14px",
          textAlign: "left",
        },
        "& th": {
          background: headBg,
          color: headColor,
          fontWeight: "600",
          fontSize: "12px",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        },
        "& tr:last-child td": { borderBottom: "none" },
        "& tbody tr:hover td": { background: rowHoverBg },
        "& td:first-of-type": { fontWeight: "600", fontFamily: MONO, fontSize: "13px" },
      }}
    >
      <table>{children}</table>
    </Box>
  );
}

function buildComponents(isDark, openLightbox) {
  const bodyColor = isDark ? "#999999" : "#444444";
  const headingColor = isDark ? "#f0f0f0" : "#0c0c0c";
  const quoteColor = isDark ? "#777" : "#aaa";
  const quoteBorder = isDark ? "#333" : "#ddd";
  const hrColor = isDark ? "#2a2a2a" : "#e8e8e8";
  const linkColor = isDark ? "#60a5fa" : "#2563eb";

  return {
    // Override code: handles both inline and block
    code: CodeBlock,

    // Tables
    table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
    thead: ({ children }) => <thead>{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => <th>{children}</th>,
    td: ({ children }) => <td>{children}</td>,

    // Headings
    h1: ({ children }) => (
      <Text fontFamily={PJ} fontSize="24px" fontWeight="700" color={headingColor} mt={8} mb={4} lineHeight={1.3}>
        {children}
      </Text>
    ),
    h2: ({ children }) => (
      <Text fontFamily={PJ} fontSize="20px" fontWeight="700" color={headingColor} mt={7} mb={3} lineHeight={1.3}>
        {children}
      </Text>
    ),
    h3: ({ children }) => (
      <Text fontFamily={PJ} fontSize="17px" fontWeight="600" color={headingColor} mt={6} mb={2} lineHeight={1.3}>
        {children}
      </Text>
    ),

    // Paragraph
    p: ({ children }) => (
      <Text fontFamily={PJ} fontSize="17px" color={bodyColor} lineHeight="1.9" mb={4}>
        {children}
      </Text>
    ),

    // Strong / em
    strong: ({ children }) => (
      <Box as="strong" fontWeight="700" color={headingColor}>
        {children}
      </Box>
    ),
    em: ({ children }) => (
      <Box as="em" fontStyle="italic">
        {children}
      </Box>
    ),

    // Lists
    ul: ({ children }) => (
      <Box as="ul" pl={6} mb={4} sx={{ "& li": { mb: "6px" } }}>
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box as="ol" pl={6} mb={4} sx={{ "& li": { mb: "6px" } }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <Box as="li" fontFamily={PJ} fontSize="17px" color={bodyColor} lineHeight="1.9">
        {children}
      </Box>
    ),

    // Blockquote
    blockquote: ({ children }) => (
      <Box
        borderLeft="3px solid"
        borderColor={quoteBorder}
        pl={5}
        my={5}
        color={quoteColor}
        fontStyle="italic"
      >
        {children}
      </Box>
    ),

    // Horizontal rule
    hr: () => <Box h="1px" bg={hrColor} my={8} />,

    // Links — YouTube URLs get a preview card
    a: ({ href, children }) => {
      if (href && getYouTubeId(href)) {
        return <YouTubePreview href={href} />;
      }
      return (
        <Box
          as="a"
          href={href}
          color={linkColor}
          textDecoration="underline"
          textDecorationColor={isDark ? "#3b5bb5" : "#bfdbfe"}
          _hover={{ textDecorationColor: linkColor }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </Box>
      );
    },

    // Images (handles both markdown and raw HTML <img> tags)
    img: ({ src, alt, width }) => (
      <Box as="figure" my={6} textAlign="center">
        <Image
          src={src}
          alt={alt}
          maxW="100%"
          w={width ? `min(${width}px, 100%)` : undefined}
          borderRadius="8px"
          display="inline-block"
          cursor="zoom-in"
          transition="opacity 0.15s"
          _hover={{ opacity: 0.9 }}
          onClick={() => openLightbox(src, alt)}
        />
      </Box>
    ),
  };
}

function getYouTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      // Handle /embed/ID and /shorts/ID
      const m = u.pathname.match(/\/(embed|shorts)\/([^/?]+)/);
      if (m) return m[2];
    }
  } catch {}
  return null;
}

function YouTubePreview({ href }) {
  const isDark = useIsDark();
  const videoId = getYouTubeId(href);
  const [title, setTitle] = useState(null);
  const cardBg = useColorModeValue("#f9f9f9", "#1a1a1a");
  const cardBorder = useColorModeValue("#e2e2e2", "#2e2e2e");
  const titleColor = useColorModeValue("#0c0c0c", "#f0f0f0");
  const badgeBg = useColorModeValue("rgba(0,0,0,0.6)", "rgba(0,0,0,0.7)");

  useEffect(() => {
    if (!videoId) return;
    fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`)
      .then((r) => r.json())
      .then((d) => setTitle(d.title))
      .catch(() => {});
  }, [videoId]);

  if (!videoId) return null;

  const thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <Box
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      display="block"
      my={6}
      borderRadius="12px"
      overflow="hidden"
      border="1px solid"
      borderColor={cardBorder}
      bg={cardBg}
      boxShadow={isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 2px 12px rgba(0,0,0,0.08)"}
      _hover={{ transform: "translateY(-2px)", boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.5)" : "0 6px 20px rgba(0,0,0,0.12)" }}
      transition="all 0.2s"
      textDecoration="none"
    >
      {/* Thumbnail */}
      <Box position="relative" w="100%" paddingTop="56.25%" bg="#000">
        <Box
          as="img"
          src={thumbnail}
          alt={title || "YouTube video"}
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          objectFit="cover"
        />
        {/* Play button overlay */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="56px"
          h="56px"
          borderRadius="full"
          bg="rgba(255,0,0,0.9)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 2px 12px rgba(0,0,0,0.4)"
        >
          <Box
            as="span"
            display="block"
            w={0}
            h={0}
            borderTop="10px solid transparent"
            borderBottom="10px solid transparent"
            borderLeft="18px solid white"
            ml="4px"
          />
        </Box>
        {/* YouTube badge */}
        <Box
          position="absolute"
          bottom="8px"
          right="8px"
          px="8px"
          py="3px"
          borderRadius="4px"
          bg={badgeBg}
          display="flex"
          alignItems="center"
          gap="5px"
        >
          <Box as="span" fontSize="10px" color="white" fontFamily={JF} letterSpacing="0.1em" fontWeight="600" textTransform="uppercase">
            YouTube
          </Box>
        </Box>
      </Box>
      {/* Title */}
      <Box px={4} py={3}>
        <Text
          fontFamily={PJ}
          fontSize="14px"
          fontWeight="600"
          color={titleColor}
          noOfLines={2}
          lineHeight="1.5"
        >
          {title || href}
        </Text>
      </Box>
    </Box>
  );
}

function ImageLightbox({ src, alt, isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(4px)" onClick={onClose} />
      <ModalContent bg="transparent" boxShadow="none" maxW="100vw" maxH="100vh" m={0}>
        <ModalBody
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
          onClick={onClose}
          cursor="zoom-out"
        >
          <Image
            src={src}
            alt={alt}
            maxW="95vw"
            maxH="95vh"
            objectFit="contain"
            borderRadius="8px"
            boxShadow="0 25px 80px rgba(0,0,0,0.6)"
            onClick={(e) => e.stopPropagation()}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default function Post() {
  const isDark = useIsDark();
  const titleColor   = useColorModeValue("#0c0c0c", "#f0f0f0");
  const metaColor    = useColorModeValue("#aaaaaa", "#555555");
  const dividerColor = useColorModeValue("#e8e8e8", "#2a2a2a");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [lightboxImg, setLightboxImg] = useState({ src: "", alt: "" });

  const openLightbox = (src, alt) => {
    setLightboxImg({ src, alt });
    onOpen();
  };

  const postData = useLoaderData();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { title, headerImage, mainText } = postData.fields;

  const publishedDate = postData.sys?.createdAt
    ? new Date(postData.sys.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const components = buildComponents(isDark, openLightbox);

  const { description } = postData.fields;

  return (
    <Box as="article" mt={10} mb={10}>
      <Helmet>
        <title>{title} | Huy Phu</title>
        <meta name="description" content={description || title} />
        <meta property="og:title" content={`${title} | Huy Phu`} />
        <meta property="og:description" content={description || title} />
        {headerImage && <meta property="og:image" content={headerImage} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://hqphu.com/posts/${slugify(title)}`} />
        <meta name="twitter:title" content={`${title} | Huy Phu`} />
        <meta name="twitter:description" content={description || title} />
        {headerImage && <meta name="twitter:image" content={headerImage} />}
      </Helmet>
      {/* Title */}
      <Text
        fontFamily={PJ}
        fontSize="26px"
        fontWeight="700"
        color={titleColor}
        lineHeight={1.3}
        mb={4}
        textAlign="center"
      >
        {title}
      </Text>

      {/* Published date */}
      {publishedDate && (
        <Text
          fontFamily={JF}
          fontSize="11px"
          fontWeight="400"
          letterSpacing="0.22em"
          textTransform="uppercase"
          color={metaColor}
          textAlign="center"
          mb={6}
        >
          {publishedDate}
        </Text>
      )}

      {/* Thin rule */}
      <Box h="1px" bg={dividerColor} mb={8} />

      {/* Header image */}
      {headerImage && (
        <Image
          src={headerImage}
          width="100%"
          objectFit="cover"
          mb={8}
          borderRadius="2px"
        />
      )}

      {/* Body */}
      <Box>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={components}
        >
          {mainText}
        </ReactMarkdown>
      </Box>

      <ImageLightbox src={lightboxImg.src} alt={lightboxImg.alt} isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}

export const postLoader = async ({ params }) => {
  const { slug } = params;
  const post = await fetchPostBySlug(slug);
  if (!post) throw new Response("Not Found", { status: 404 });
  return post;
};
